import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Typography,
  Box,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState, useEffect } from "react";

import "@/components/membership/Dashboard/Dashboard.css";
import API_Calls_Preview from "@/api/calls/API_Calls_Preview";
import { getDashboardIcon } from "@/components/membership/Dashboard/DashboardUtils/dashboardIcons";
import ConfirmationModal from "@/components/membership/Modals/ConfirmationModal";
import UnstageFileModal from "@/components/membership/Modals/UnstageFileModal";
import {
  percentageChangeBreaksThreshold,
  getEmployerNameChangeData,
} from "@/components/membership/Preview/previewUtils";
import SummaryBody from "@/components/membership/Preview/SummaryBody";
import Notifications from "@/components/Notifications/Notifications";
import SiteIcons from "@/components/SiteIcons/SiteIcons";
import { canDeleteProcessedFile } from "@/utils/permissionsHandler";
import { parseAdaPath } from "@/utils/util";

function SummaryModal({
  row,
  handleDeleteFile,
  handleConfirmFile,
  handleUnstagedFile,
  handleIEBChange,
}) {
  const [open, setOpen] = useState(false);
  const [isConfirmable, setIsConfirmable] = useState(false);
  const [isDeletable, setIsDeletable] = useState(false);
  const [openUnstageModal, setOpenUnstageModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [badFields, setBadFields] = useState([]);
  const [file, setFile] = useState(row);
  const [parsedAdaFile, setParsedAdaFile] = useState({});
  const { user } = useAuth0();
  const [notification, setNotification] = useState({});
  const { confirmPreviewSummary, getPreviewSummary, unstageFromVan } =
    API_Calls_Preview();
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    setFile(row);
    setParsedAdaFile(parseAdaPath(row.ada_file));
  }, [row]);

  useEffect(() => {
    setIsConfirmable(file.allow_confirm);
    setIsDeletable(canDeleteProcessedFile(user) && !file.confirmed);
  }, [file, user]);

  const fetchPreviewSummary = async () => {
    const affiliate = parseAdaPath(row.ada_file).affiliate;
    const adaFile = row.ada_file.split("/").slice(2, -1).join("/");
    const previewResponse = await getPreviewSummary(affiliate, adaFile);
    setSummaryData(previewResponse);
  };

  const handleClickOpen = () => {
    fetchPreviewSummary();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenUnstageModal(false);
    setSummaryData(null);
  };

  const handleConfirmClick = async () => {
    const badFields = [];
    const employerNameData = getEmployerNameChangeData(summaryData.data);
    if (
      percentageChangeBreaksThreshold(summaryData.data.record_count_changes) &&
      summaryData.data.use === "general"
    ) {
      badFields.push("Number of member rows");
    }
    if (percentageChangeBreaksThreshold(employerNameData)) {
      badFields.push("Number of changed or new employer names");
    }
    if (badFields.length > 0) {
      setBadFields(badFields);
      setOpenConfirmationModal(true);
    } else {
      confirmFile();
    }
  };

  const handleFileUnstage = async (reason) => {
    // make unstage call
    const affiliate = parsedAdaFile.affiliate;
    const adaFile = file.ada_file.split("/").slice(2, -1).join("/");
    const unstageResponse = await unstageFromVan(affiliate, adaFile, reason);
    // on success, success messaging, callback to set file as VAN: "unstaged"
    if (!unstageResponse.error) {
      setNotification({
        message: "This file has been unstaged from VAN.",
        severity: "success",
        duration: 3000,
      });
      handleUnstagedFile(file);
    } else {
      console.error(
        "Unable to unstaged:",
        unstageResponse.message || "Unknown error"
      );
      setNotification({
        message: "Unable to unstage from VAN at this time",
        severity: "error",
        duration: 3000,
      });
    }
    handleClose();
  };

  const confirmFile = async () => {
    const affiliate = parsedAdaFile.affiliate;
    const adaFile = file.ada_file.split("/").slice(2, -1).join("/");
    const confirmationResponse = await confirmPreviewSummary(
      affiliate,
      adaFile
    );
    if (!confirmationResponse.error) {
      setNotification({
        message:
          "File confirmed. Please allow up to 15 minutes for the Dashboard to update",
        severity: "success",
        duration: null,
      });
      // callback to SubmittedFilesTable to remove this file from list in the UI while the Dashboard processes
      handleConfirmFile(file);
    } else {
      console.error(
        "Unable to confirm at this time:",
        confirmationResponse.message || "Unknown error"
      );
      setNotification({
        message: "Unable to confirm at this time",
        severity: "error",
        duration: 3000,
      });
    }
    handleClose();
  };

  const handleDeleteClick = () => {
    handleDeleteFile(file.ada_file);
    handleClose();
  };

  return (
    <>
      {getDashboardIcon(file, "validation", handleClickOpen)}

      <Dialog
        onClose={handleClose}
        open={open}
        data-testid="summary-modal"
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "none",
          },
        }}
      >
        <>
          <DialogTitle className="seiu-modal-header">
            {file.confirmed
              ? `This file was confirmed on: ${file.confirmed.replace(
                  "T",
                  " "
                )}`
              : "This file has not been confirmed"}
          </DialogTitle>
          <DialogContent dividers>
            {summaryData?.error ? (
              <Box>
                <Typography sx={{ fontStyle: "italic" }}>
                  No summary available for this file
                </Typography>
              </Box>
            ) : summaryData?.data ? (
              <SummaryBody
                isModal={true}
                fileData={parsedAdaFile}
                summaryData={{
                  summary: summaryData.data,
                }}
                IEBApproved={row.IEB_approved}
                handleIEBChange={handleIEBChange}
              />
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <SiteIcons
                  iconstring="circular-loader"
                  size="3rem"
                  page="sitewide-loader"
                  sx={{ marginTop: "30px" }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "space-between !important",
            }}
          >
            {/* all the left-aligned buttons */}
            <Box>
              {!file.confirmed && (
                <>
                  <Button
                    disabled={!isConfirmable}
                    className="siteBlue"
                    onClick={handleConfirmClick}
                  >
                    Confirm Now
                  </Button>
                  <Button
                    disabled={!isDeletable}
                    className="siteRed"
                    onClick={handleDeleteClick}
                  >
                    Delete
                  </Button>
                </>
              )}

              {file.VAN === "unstaged" ? (
                <Typography>
                  <i>File has been unstaged from VAN</i>
                </Typography>
              ) : (
                file.confirmed &&
                !file.VAN &&
                (file.use === "general" || !file.use) && (
                  <Button
                    className="siteBlue"
                    onClick={() => setOpenUnstageModal(true)}
                  >
                    Cancel VAN Push
                  </Button>
                )
              )}
            </Box>

            <Button
              // Focus the safe (Close) button on open
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className="siteBlueOutlined"
              onClick={handleClose}
            >
              Close
            </Button>
          </DialogActions>
          <UnstageFileModal
            open={openUnstageModal}
            setOpenUnstageModal={setOpenUnstageModal}
            unstageFile={handleFileUnstage}
          />
          <ConfirmationModal
            open={openConfirmationModal}
            handleClose={setOpenConfirmationModal}
            confirmFile={confirmFile}
            badFields={badFields}
          />
        </>
      </Dialog>

      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
}

SummaryModal.propTypes = {
  row: PropTypes.object,
  handleDeleteFile: PropTypes.func,
  handleConfirmFile: PropTypes.func,
};

export default SummaryModal;
